import { alpha, type Theme } from "@mui/material/styles";
import type { MatchWithBlockInfo } from "../../../types";
import type { SxProps } from "@mui/material/styles";

type MatchStatus = MatchWithBlockInfo["status"];

const STATUS_ACCENT: Record<string, string> = {
  DOUBLON: "#e879f9",
  LIBERE: "#22d3ee",
  ENCOURS: "#4ade80",
  CREE: "#94a3b8",
};

function accentFor(status: MatchStatus): string | null {
  if (!status) return null;
  return STATUS_ACCENT[status] ?? null;
}

/**
 * Fond de ligne :
 * - clair : pastels historiques (texte sombre lisible)
 * - sombre : teinte très légère + contraste texte préservé (évite les verts/roses saturés)
 */
export function getMatchRowBackgroundColor(
  status: MatchStatus,
  theme: Theme
): string {
  const isDark = theme.palette.mode === "dark";
  if (isDark) {
    const accent = accentFor(status);
    if (accent && status !== "CREE") {
      return alpha(accent, 0.1);
    }
    return theme.palette.background.paper;
  }
  if (status === "DOUBLON") return "#ffccff";
  if (status === "LIBERE") return "#b3f0ff";
  if (status === "ENCOURS") return "#b3ffb3";
  return "#ffffff";
}

export function getMatchRowHoverBackgroundColor(
  status: MatchStatus,
  theme: Theme
): string {
  const isDark = theme.palette.mode === "dark";
  if (isDark) {
    const accent = accentFor(status);
    if (accent && status !== "CREE") {
      return alpha(accent, 0.16);
    }
    return alpha(theme.palette.common.white, 0.06);
  }
  if (status === "DOUBLON") return "#f0b3f0";
  if (status === "LIBERE") return "#9ce6f7";
  if (status === "ENCOURS") return "#99f099";
  return "#eaeaea";
}

/** Styles de ligne : en sombre, barre latérale de statut pour signaler sans casser le contraste. */
export function getMatchRowSx(
  status: MatchStatus,
  theme: Theme,
  options?: { highlighted?: boolean }
): SxProps<Theme> {
  const isDark = theme.palette.mode === "dark";
  const accent = accentFor(status);
  const bg = getMatchRowBackgroundColor(status, theme);
  const hover = getMatchRowHoverBackgroundColor(status, theme);

  return {
    backgroundColor: bg,
    color: "text.primary",
    borderBottomColor: "divider",
    ...(isDark && accent
      ? {
          borderLeft: `3px solid ${alpha(accent, 0.9)}`,
        }
      : {
          borderLeft: "3px solid transparent",
        }),
    "& .MuiTableCell-root": {
      color: "text.primary",
      borderBottomColor: "divider",
    },
    "&:hover": {
      backgroundColor: options?.highlighted ? bg : hover,
    },
  };
}
